import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ShoppingBag, Plus, Search, Tag, MapPin, Filter, X, Check, Camera, Loader2, ArrowUpRight,
  MessageCircle, User, Clock, Package, Star, ChevronDown, AlertTriangle, Trash2, ShieldAlert,
  Image as ImageIcon, DollarSign, Globe, Heart, Share2, Copy, ExternalLink, Rocket, Wallet
} from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useAppContext } from '../hooks/useAppContext';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { uploadImage } from '../lib/imageUpload';
import { BCHLogo } from '../components/Layout';
import VerifiedBadge from '../components/VerifiedBadge';
import { useTipWallet } from '../hooks/useTipWallet';
import { BOOST_WALLET, BOOST_KIND, FEATURED_LISTING_TAG, FEATURED_LISTING_TIERS } from '../lib/boost';
import { useAccountMaturity } from '../hooks/useTrustScore';
import { canCreateMerchantListing, ACCOUNT_AGE } from '../lib/trustScore';

// ── Constants ──────────────────────────────────────────────────────
const LISTING_KIND = 30402; // Addressable event for classifieds
const LISTING_TAG = 'bch-marketplace';

const ADMIN_PUBKEYS = [
  'df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc',
];

const CATEGORIES = [
  'All',
  'Electronics',
  'Clothing & Accessories',
  'Home & Garden',
  'Books & Media',
  'Vehicles',
  'Collectibles & Art',
  'Sports & Outdoors',
  'Toys & Games',
  'Services',
  'Crypto & Finance',
  'Food & Drink',
  'Health & Beauty',
  'Other',
];

const CONDITIONS = ['New', 'Like New', 'Good', 'Fair', 'For Parts'];

interface ListingData {
  title: string;
  description: string;
  price: number;
  currency: 'BCH' | 'USD';
  condition: string;
  category: string;
  location: string;
  images: string[];
  shipping?: string;
  sold?: boolean;
}

// ── Main Page ──────────────────────────────────────────────────────
export default function MarketplacePage() {
  const { user, login } = useAppContext();
  const { price: bchPrice } = useBCHPrice();
  const maturity = useAccountMaturity(user?.pubkey);
  const canList = maturity.isOperator || maturity.loading || (maturity.firstSeen > 0 && canCreateMerchantListing(maturity.firstSeen));
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedListing, setSelectedListing] = useState<any | null>(null);
  const [conditionFilter, setConditionFilter] = useState('All');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'price_asc' | 'price_desc'>('newest');
  const [tab, setTab] = useState<'browse' | 'mine'>('browse');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [featuredListing, setFeaturedListing] = useState<any | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);
  const [searchParams] = useSearchParams();

  const isAdmin = !!user && ADMIN_PUBKEYS.includes(user.pubkey);

  // Fetch listings
  const { events: listings, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [LISTING_KIND], '#t': [LISTING_TAG], limit: 200 }],
  });

  // Auto-open listing from shared link (?listing=naddr...)
  useEffect(() => {
    const naddrParam = searchParams.get('listing');
    if (!naddrParam || loading || listings.length === 0) return;
    try {
      const decoded = nip19.decode(naddrParam);
      if (decoded.type !== 'naddr') return;
      const { pubkey, identifier } = decoded.data;
      const match = listings.find(ev => {
        const d = ev.tags.find((t: string[]) => t[0] === 'd')?.[1];
        return ev.pubkey === pubkey && d === identifier;
      });
      if (match) {
        const data: ListingData = JSON.parse(match.content);
        const dTag = match.tags.find((t: string[]) => t[0] === 'd')?.[1] || '';
        setSelectedListing({ ...data, event: match, pubkey: match.pubkey, dTag });
      }
    } catch {}
  }, [searchParams, loading, listings]);

  // Fetch featured boost events
  const { events: featuredEvents } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [FEATURED_LISTING_TAG], limit: 100 }],
  });

  const featuredIds = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const ids = new Set<string>();
    featuredEvents.forEach(ev => {
      try {
        const data = JSON.parse(ev.content);
        if (data.expiresAt > now) ids.add(data.listingDTag ? `${LISTING_KIND}:${ev.pubkey}:${data.listingDTag}` : data.eventId);
      } catch {}
    });
    return ids;
  }, [featuredEvents]);

  // Fetch deletion events
  const deletionAuthors = user ? [...new Set([...ADMIN_PUBKEYS, user.pubkey])] : ADMIN_PUBKEYS;
  const { events: deletionEvents } = useNostrQuery({
    filters: deletionAuthors.length > 0
      ? [{ kinds: [5], authors: deletionAuthors, '#k': [String(LISTING_KIND)], limit: 200 }]
      : [],
    enabled: deletionAuthors.length > 0,
  });

  const deletedIds = useMemo(() => {
    const ids = new Set<string>();
    deletionEvents.forEach(ev => {
      ev.tags.forEach((t: string[]) => {
        if (t[0] === 'e') ids.add(t[1]);
        if (t[0] === 'a') ids.add(t[1]);
      });
    });
    return ids;
  }, [deletionEvents]);

  const [localDeletedIds, setLocalDeletedIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('bchnostr:deleted-marketplace');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  const handleDeleted = useCallback((eventId: string, aTag: string) => {
    setLocalDeletedIds(prev => {
      const updated = new Set(prev);
      updated.add(eventId);
      updated.add(aTag);
      try { localStorage.setItem('bchnostr:deleted-marketplace', JSON.stringify([...updated])); } catch {}
      return updated;
    });
  }, []);

  // Parse and filter listings
  const parsed = useMemo(() => {
    return listings
      .map(ev => {
        try {
          const data: ListingData = JSON.parse(ev.content);
          if (!data.title) return null;
          const dTag = ev.tags.find(t => t[0] === 'd')?.[1] || '';
          return { ...data, event: ev, pubkey: ev.pubkey, dTag };
        } catch { return null; }
      })
      .filter((l): l is ListingData & { event: any; pubkey: string; dTag: string } => {
        if (!l) return false;
        if (deletedIds.has(l.event.id)) return false;
        if (l.dTag && deletedIds.has(`${LISTING_KIND}:${l.pubkey}:${l.dTag}`)) return false;
        if (localDeletedIds.has(l.event.id)) return false;
        if (l.dTag && localDeletedIds.has(`${LISTING_KIND}:${l.pubkey}:${l.dTag}`)) return false;
        // Filters
        const q = searchQuery.toLowerCase();
        if (q && !l.title.toLowerCase().includes(q) && !l.description?.toLowerCase().includes(q)) return false;
        if (category !== 'All' && l.category !== category) return false;
        if (conditionFilter !== 'All' && l.condition !== conditionFilter) return false;
        // Price range filter (compare in USD)
        const priceUSD = l.currency === 'USD' ? l.price : (bchPrice ? l.price * bchPrice : l.price);
        if (minPrice !== '' && priceUSD < parseFloat(minPrice)) return false;
        if (maxPrice !== '' && priceUSD > parseFloat(maxPrice)) return false;
        return true;
      })
      .sort((a, b) => {
        // Featured always first
        const aFeatured = featuredIds.has(a.event.id) || featuredIds.has(`${LISTING_KIND}:${a.pubkey}:${a.dTag}`);
        const bFeatured = featuredIds.has(b.event.id) || featuredIds.has(`${LISTING_KIND}:${b.pubkey}:${b.dTag}`);
        if (aFeatured && !bFeatured) return -1;
        if (!aFeatured && bFeatured) return 1;
        if (sortBy === 'price_asc') {
          const aUSD = a.currency === 'USD' ? a.price : (bchPrice ? a.price * bchPrice : 0);
          const bUSD = b.currency === 'USD' ? b.price : (bchPrice ? b.price * bchPrice : 0);
          return aUSD - bUSD;
        }
        if (sortBy === 'price_desc') {
          const aUSD = a.currency === 'USD' ? a.price : (bchPrice ? a.price * bchPrice : 0);
          const bUSD = b.currency === 'USD' ? b.price : (bchPrice ? b.price * bchPrice : 0);
          return bUSD - aUSD;
        }
        if (sortBy === 'oldest') return a.event.created_at - b.event.created_at;
        return b.event.created_at - a.event.created_at;
      });
  }, [listings, deletedIds, localDeletedIds, searchQuery, category, conditionFilter, sortBy, bchPrice, minPrice, maxPrice, featuredIds]);

  const visibleListings = tab === 'mine' && user
    ? parsed.filter(l => l.pubkey === user.pubkey)
    : parsed;

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-primary" /> Marketplace
            </h1>
            {user && (
              <div className="flex bg-muted rounded-lg p-0.5 text-xs font-medium">
                <button
                  onClick={() => setTab('browse')}
                  className={`px-3 py-1 rounded-md transition-colors ${tab === 'browse' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
                >
                  Browse
                </button>
                <button
                  onClick={() => setTab('mine')}
                  className={`px-3 py-1 rounded-md transition-colors ${tab === 'mine' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
                >
                  My Listings
                </button>
              </div>
            )}
          </div>
          {user && canList && (
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" /> Sell Item
            </button>
          )}
          {user && !canList && !maturity.loading && (
            <div className="text-[10px] text-muted-foreground bg-muted rounded-lg px-3 py-1.5 max-w-[180px] text-center">
              Listings unlock after {ACCOUNT_AGE.merchantMinDays} days
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search marketplace..."
            className="w-full bg-muted rounded-lg pl-10 pr-4 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                category === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Condition filter row */}
        <div className="flex gap-1.5 mt-2 overflow-x-auto scrollbar-hide">
          {['All', ...CONDITIONS].map(c => (
            <button
              key={c}
              onClick={() => setConditionFilter(c)}
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${
                conditionFilter === c ? 'bg-emerald-500 text-white' : 'bg-muted/60 text-muted-foreground hover:text-foreground'
              }`}
            >
              {c === 'All' ? 'Any Condition' : c}
            </button>
          ))}
        </div>

        {/* Sort row */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[11px] text-muted-foreground shrink-0">Sort:</span>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
            {([
              { val: 'newest', label: 'Newest' },
              { val: 'oldest', label: 'Oldest' },
              { val: 'price_asc', label: 'Price ↑' },
              { val: 'price_desc', label: 'Price ↓' },
            ] as const).map(opt => (
              <button
                key={opt.val}
                onClick={() => setSortBy(opt.val)}
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${
                  sortBy === opt.val ? 'bg-primary text-primary-foreground' : 'bg-muted/60 text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Price range row */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[11px] text-muted-foreground shrink-0">Price ($):</span>
          <input
            type="number"
            value={minPrice}
            onChange={e => setMinPrice(e.target.value)}
            placeholder="Min"
            min="0"
            className="w-20 bg-muted rounded-md px-2 py-0.5 text-[11px] border border-border focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <span className="text-[11px] text-muted-foreground">–</span>
          <input
            type="number"
            value={maxPrice}
            onChange={e => setMaxPrice(e.target.value)}
            placeholder="Max"
            min="0"
            className="w-20 bg-muted rounded-md px-2 py-0.5 text-[11px] border border-border focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {(minPrice || maxPrice) && (
            <button
              onClick={() => { setMinPrice(''); setMaxPrice(''); }}
              className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-0.5"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mx-4 mt-3 mb-1 rounded-lg bg-amber-500/5 border border-amber-500/20 px-3 py-2">
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          <strong className="text-amber-600 dark:text-amber-400">Disclaimer:</strong> BCHNostr is a peer-to-peer marketplace. All transactions are between buyers and sellers directly.
          BCHNostr is not responsible for lost, damaged, or stolen goods. All sales are final — no refunds are provided by BCHNostr.
          Exercise caution and due diligence before making any purchase.
        </p>
      </div>

      {/* Create Listing Form */}
      {showCreate && (
        <CreateListingForm
          onCreated={() => { setShowCreate(false); refetch(); showToast('✓ Listing published!'); }}
          onClose={() => setShowCreate(false)}
        />
      )}

      {/* Listing Detail Modal */}
      {selectedListing && (
        <ListingDetail
          listing={selectedListing}
          bchPrice={bchPrice}
          user={user}
          isAdmin={isAdmin}
          isFeatured={featuredIds.has(selectedListing.event.id) || featuredIds.has(`${LISTING_KIND}:${selectedListing.pubkey}:${selectedListing.dTag}`)}
          onFeature={() => { setSelectedListing(null); setFeaturedListing(selectedListing); }}
          onClose={() => setSelectedListing(null)}
          onDeleted={handleDeleted}
        />
      )}

      {/* Listings Grid */}
      <div className="p-4">
        {!user && !loading && (
          <div className="mb-4 rounded-xl border border-primary/20 bg-primary/5 px-4 py-4 flex flex-col sm:flex-row items-center gap-3">
            <div className="flex-1 text-center sm:text-left">
              <p className="font-semibold text-sm">Buy &amp; sell with Bitcoin Cash</p>
              <p className="text-xs text-muted-foreground mt-0.5">Log in to message sellers, pay with BCH, and list your own items.</p>
            </div>
            <button
              onClick={login}
              className="shrink-0 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Log In / Sign Up
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : visibleListings.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">
              {tab === 'mine' ? "You haven't listed anything yet." : 'No listings found.'}
            </p>
            {user && (
              <button onClick={() => setShowCreate(true)} className="text-sm text-primary hover:underline mt-2">
                {tab === 'mine' ? 'List your first item!' : 'Be the first to list something!'}
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {visibleListings.map(listing => {
              const isFeatured = featuredIds.has(listing.event.id) || featuredIds.has(`${LISTING_KIND}:${listing.pubkey}:${listing.dTag}`);
              return (
                <ListingCard
                  key={listing.event.id}
                  listing={listing}
                  bchPrice={bchPrice}
                  isFeatured={isFeatured}
                  onClick={() => setSelectedListing(listing)}
                  onFeature={user && listing.pubkey === user.pubkey && !isFeatured ? () => setFeaturedListing(listing) : undefined}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Featured Listing Modal */}
      {featuredListing && (
        <FeaturedListingModal
          listing={featuredListing}
          onClose={() => setFeaturedListing(null)}
          onSuccess={() => { setFeaturedListing(null); showToast('✨ Listing featured!'); refetch(); }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-medium shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2">
          <Check className="w-4 h-4 shrink-0" /> {toast}
        </div>
      )}
    </div>
  );
}

// ── Listing Card ───────────────────────────────────────────────────
function ListingCard({ listing, bchPrice, isFeatured, onClick, onFeature }: {
  listing: any; bchPrice: number | null; isFeatured?: boolean;
  onClick: () => void; onFeature?: () => void;
}) {
  const { profile } = useNostrProfile(listing.pubkey);
  const priceDisplay = formatPrice(listing.price, listing.currency, bchPrice);
  const timeAgo = getTimeAgo(listing.event.created_at);

  return (
    <div className={`glass-card text-left transition-all w-full relative ${isFeatured ? 'border-amber-400/50 shadow-amber-400/10 shadow-md' : listing.sold ? 'opacity-60' : ''}`}>
      {isFeatured && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-amber-400 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
          <Star className="w-2.5 h-2.5 fill-amber-900" /> Featured
        </div>
      )}
      <button onClick={onClick} className="w-full text-left active:scale-[0.99] transition-transform">
      {/* Image */}
      {listing.images?.[0] ? (
        <div className="relative mb-2.5">
          <img
            src={listing.images[0]}
            alt={listing.title}
            className="w-full h-40 object-cover rounded-lg bg-muted"
            loading="lazy"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          {listing.sold && (
            <div className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center">
              <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full rotate-[-8deg]">SOLD</span>
            </div>
          )}
        </div>
      ) : (
        <div className="relative w-full h-28 rounded-lg bg-muted/60 flex items-center justify-center mb-2.5">
          <Package className="w-8 h-8 text-muted-foreground/30" />
          {listing.sold && (
            <div className="absolute inset-0 rounded-lg bg-black/30 flex items-center justify-center">
              <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full rotate-[-8deg]">SOLD</span>
            </div>
          )}
        </div>
      )}

      {/* Price + condition badge */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-lg font-bold text-primary">{priceDisplay}</span>
        {listing.condition && (
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
            listing.condition === 'New' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            : listing.condition === 'Like New' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
            : 'bg-muted text-muted-foreground'
          }`}>
            {listing.condition}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="font-semibold text-sm line-clamp-2 leading-snug mb-1.5">{listing.title}</h3>

      {/* Meta row */}
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
        {listing.location && (
          <span className="flex items-center gap-0.5 truncate">
            <MapPin className="w-3 h-3 shrink-0" /> {listing.location}
          </span>
        )}
        <span className="flex items-center gap-0.5 shrink-0">
          <Clock className="w-3 h-3" /> {timeAgo}
        </span>
      </div>

      {/* Seller */}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/50">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
        ) : (
          <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-3 h-3 text-primary/60" />
          </div>
        )}
        <span className="text-[11px] text-muted-foreground truncate">
          {profile?.display_name || profile?.name || listing.pubkey.slice(0, 8) + '...'}
        </span>
        <VerifiedBadge nip05={profile?.nip05} pubkey={listing.pubkey} size="xs" />
      </div>
      </button>
      {onFeature && (
        <button
          onClick={e => { e.stopPropagation(); onFeature(); }}
          className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-amber-400/40 text-amber-600 dark:text-amber-400 text-[11px] font-medium hover:bg-amber-400/10 transition-colors"
        >
          <Rocket className="w-3 h-3" /> Feature this listing
        </button>
      )}
    </div>
  );
}

// ── Listing Detail Modal ───────────────────────────────────────────
function ListingDetail({ listing, bchPrice, user, isAdmin, isFeatured, onFeature, onClose, onDeleted }: {
  listing: any; bchPrice: number | null; user: any; isAdmin: boolean;
  isFeatured?: boolean; onFeature?: () => void;
  onClose: () => void; onDeleted: (eventId: string, aTag: string) => void;
}) {
  const { profile } = useNostrProfile(listing.pubkey);
  const { address: sellerBchAddr } = useBCHAddress(listing.pubkey);
  const { publish } = useNostrPublish();
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [markingSold, setMarkingSold] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentImg, setCurrentImg] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const isOwn = user && listing.pubkey === user.pubkey;
  const canDelete = isOwn || isAdmin;
  const priceDisplay = formatPrice(listing.price, listing.currency, bchPrice);
  const timeAgo = getTimeAgo(listing.event.created_at);
  const sellerName = profile?.display_name || profile?.name || listing.pubkey.slice(0, 12) + '...';

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const aTag = `${LISTING_KIND}:${listing.pubkey}:${listing.dTag}`;
      await publish({
        kind: 5,
        content: isAdmin && !isOwn ? 'Removed by admin' : '',
        tags: [['e', listing.event.id], ['a', aTag], ['k', String(LISTING_KIND)]],
        created_at: Math.floor(Date.now() / 1000),
      });
      onDeleted(listing.event.id, aTag);
      onClose();
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setDeleting(false);
  };

  const handleMarkSold = async () => {
    setMarkingSold(true);
    try {
      const updatedData = { ...listing, sold: !listing.sold };
      delete updatedData.event;
      delete updatedData.pubkey;
      delete updatedData.dTag;
      await publish({
        kind: LISTING_KIND,
        content: JSON.stringify(updatedData),
        tags: [
          ['d', listing.dTag],
          ['t', LISTING_TAG],
          ['t', 'bch'],
          ['t', 'marketplace'],
          ['title', listing.title],
          ['price', String(listing.price), listing.currency],
          ...(listing.location ? [['location', listing.location]] : []),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      onClose();
    } catch (err) {
      console.error('Mark sold failed:', err);
    }
    setMarkingSold(false);
  };

  const handleShare = () => {
    try {
      const naddr = nip19.naddrEncode({
        kind: LISTING_KIND,
        pubkey: listing.pubkey,
        identifier: listing.dTag,
      });
      const url = `${window.location.origin}/marketplace?listing=${naddr}`;
      navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch {}
  };

  const handleCopyAddress = () => {
    if (!sellerBchAddr) return;
    navigator.clipboard.writeText(sellerBchAddr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Convert price to BCH for payment link
  const priceBCH = listing.currency === 'BCH'
    ? listing.price
    : bchPrice ? listing.price / bchPrice : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-lg max-h-[90dvh] bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-xl overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Images */}
        {listing.images?.length > 0 ? (
          <div
            className="relative"
            onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              if (touchStartX.current === null) return;
              const dx = e.changedTouches[0].clientX - touchStartX.current;
              touchStartX.current = null;
              if (Math.abs(dx) < 40) return;
              const total = listing.images.length;
              setCurrentImg(i => dx < 0 ? Math.min(i + 1, total - 1) : Math.max(i - 1, 0));
            }}
          >
            <img
              src={listing.images[currentImg]}
              alt={listing.title}
              className="w-full h-56 sm:h-72 object-cover select-none"
            />
            {listing.images.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {listing.images.map((_: string, i: number) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImg(i)}
                    className={`w-2 h-2 rounded-full transition-all ${i === currentImg ? 'bg-white scale-125' : 'bg-white/50'}`}
                  />
                ))}
              </div>
            )}
            <div className="absolute top-3 right-3 flex gap-1.5">
              <button onClick={handleShare} className="p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70">
                {shared ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4" />}
              </button>
              <button onClick={onClose} className="p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between px-4 pt-4">
            <div />
            <div className="flex gap-1.5">
              <button onClick={handleShare} className="p-1.5 rounded-full hover:bg-muted">
                {shared ? <Check className="w-4 h-4 text-emerald-500" /> : <Share2 className="w-4 h-4 text-muted-foreground" />}
              </button>
              <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        <div className="p-4 space-y-4">
          {/* Price + condition */}
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-primary">{priceDisplay}</span>
            <div className="flex items-center gap-2">
              {listing.condition && (
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  listing.condition === 'New' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                  : listing.condition === 'Like New' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                  : 'bg-muted text-muted-foreground'
                }`}>
                  {listing.condition}
                </span>
              )}
              {listing.sold && (
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-500/10 text-red-500">SOLD</span>
              )}
            </div>
          </div>

          {/* Title + description */}
          <div>
            <h2 className="text-lg font-bold leading-snug">{listing.title}</h2>
            {listing.description && (
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{listing.description}</p>
            )}
          </div>

          {/* Details chips */}
          <div className="flex flex-wrap gap-2">
            {listing.category && (
              <span className="flex items-center gap-1 text-xs bg-muted px-2.5 py-1 rounded-full">
                <Tag className="w-3 h-3" /> {listing.category}
              </span>
            )}
            {listing.location && (
              <span className="flex items-center gap-1 text-xs bg-muted px-2.5 py-1 rounded-full">
                <MapPin className="w-3 h-3" /> {listing.location}
              </span>
            )}
            {listing.shipping && (
              <span className="flex items-center gap-1 text-xs bg-muted px-2.5 py-1 rounded-full">
                <Package className="w-3 h-3" /> {listing.shipping}
              </span>
            )}
            <span className="flex items-center gap-1 text-xs bg-muted px-2.5 py-1 rounded-full">
              <Clock className="w-3 h-3" /> Listed {timeAgo}
            </span>
          </div>

          {/* Seller card */}
          <div className="rounded-xl border border-border p-3">
            <Link
              to={`/profile/${listing.pubkey}`}
              onClick={onClose}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              {profile?.picture ? (
                <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary/60" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{sellerName}</p>
                  <VerifiedBadge nip05={profile?.nip05} pubkey={listing.pubkey} size="xs" />
                </div>
                <p className="text-[11px] text-muted-foreground">Seller</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
            </Link>
          </div>

          {/* Action buttons */}
          {!isOwn && user && (
            <div className="flex gap-2">
              <Link
                to={`/messages?to=${listing.pubkey}`}
                onClick={onClose}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Message Seller
              </Link>
              {sellerBchAddr && priceBCH && (
                <a
                  href={`${sellerBchAddr}?amount=${priceBCH.toFixed(8)}&message=${encodeURIComponent(listing.title)}`}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors"
                >
                  <BCHLogo size={16} /> Pay
                </a>
              )}
            </div>
          )}
          {!isOwn && !user && (
            <p className="text-xs text-center text-muted-foreground py-2">Log in to message the seller or pay with BCH.</p>
          )}

          {/* Copy BCH address */}
          {sellerBchAddr && !isOwn && (
            <button
              onClick={handleCopyAddress}
              className="w-full flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
            >
              <BCHLogo size={14} />
              <code className="flex-1 text-[11px] font-mono text-muted-foreground truncate text-left">
                {sellerBchAddr.replace(/^bitcoincash:/i, '')}
              </code>
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <Copy className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
            </button>
          )}

          {/* Feature listing */}
          {isOwn && onFeature && (
            <button
              onClick={onFeature}
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                isFeatured
                  ? 'border-amber-400/40 bg-amber-400/5 text-amber-600 dark:text-amber-400 cursor-default'
                  : 'border-amber-400/50 text-amber-600 dark:text-amber-400 hover:bg-amber-400/10'
              }`}
              disabled={isFeatured}
            >
              <Rocket className="w-4 h-4" />
              {isFeatured ? '✨ Currently Featured' : '✨ Feature this Listing'}
            </button>
          )}

          {/* Mark as Sold */}
          {isOwn && (
            <button
              onClick={handleMarkSold}
              disabled={markingSold}
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-colors disabled:opacity-50 ${
                listing.sold
                  ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10'
                  : 'border-border text-muted-foreground hover:border-red-400 hover:text-red-500'
              }`}
            >
              {markingSold ? <Loader2 className="w-4 h-4 animate-spin" /> : listing.sold ? <Check className="w-4 h-4" /> : <Package className="w-4 h-4" />}
              {listing.sold ? 'Mark as Available' : 'Mark as Sold'}
            </button>
          )}

          {/* Delete */}
          {canDelete && (
            <div className="pt-2 border-t border-border">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-red-500/30 text-red-500 text-sm font-medium hover:bg-red-500/10 transition-colors"
                >
                  {isAdmin && !isOwn ? <ShieldAlert className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                  {isAdmin && !isOwn ? 'Remove (admin)' : 'Delete Listing'}
                </button>
              ) : (
                <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 space-y-2">
                  <p className="text-xs text-center text-red-500 font-medium">Delete this listing permanently?</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 py-2 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-medium disabled:opacity-50 transition-colors"
                    >
                      {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      {deleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Disclaimer */}
          <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 px-3 py-2.5 mt-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                BCHNostr is not responsible for lost, damaged, or stolen goods. All sales are final.
                No refunds are provided by BCHNostr. Transactions are directly between buyer and seller.
                Exercise caution and due diligence.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Create Listing Form ────────────────────────────────────────────
function CreateListingForm({ onCreated, onClose }: { onCreated: () => void; onClose: () => void }) {
  const { publish } = useNostrPublish();
  const { price: bchPrice } = useBCHPrice();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState<'USD' | 'BCH'>('USD');
  const [condition, setCondition] = useState('Good');
  const [category, setCategory] = useState('Other');
  const [location, setLocation] = useState('');
  const [shipping, setShipping] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (images.length >= 5) { setError('Maximum 5 images'); return; }
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
      const url = await uploadImage(file, setUploadProgress);
      setImages(prev => [...prev, url]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePublish = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (!price.trim() || isNaN(parseFloat(price)) || parseFloat(price) <= 0) { setError('Enter a valid price'); return; }

    setSaving(true);
    setError(null);
    try {
      const dTag = `listing-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const data: ListingData = {
        title: title.trim(),
        description: description.trim(),
        price: parseFloat(price),
        currency,
        condition,
        category,
        location: location.trim(),
        images,
        ...(shipping.trim() ? { shipping: shipping.trim() } : {}),
      };

      await publish({
        kind: LISTING_KIND,
        content: JSON.stringify(data),
        tags: [
          ['d', dTag],
          ['t', LISTING_TAG],
          ['t', 'bch'],
          ['t', 'marketplace'],
          ['title', data.title],
          ['price', String(data.price), data.currency],
          ...(data.location ? [['location', data.location]] : []),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish listing');
    } finally {
      setSaving(false);
    }
  };

  const priceConversion = (() => {
    if (!price || isNaN(parseFloat(price)) || !bchPrice) return null;
    const val = parseFloat(price);
    if (currency === 'USD') return `${(val / bchPrice).toFixed(6)} BCH`;
    return `$${(val * bchPrice).toFixed(2)} USD`;
  })();

  return (
    <div className="border-b border-border p-4 bg-muted/30 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <ShoppingBag className="w-4 h-4 text-primary" /> List an Item for Sale
        </h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
      </div>

      {/* Title */}
      <input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="What are you selling?"
        className="w-full bg-background rounded-lg px-3 py-2.5 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary"
        maxLength={120}
      />

      {/* Description */}
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Describe the item — details, features, flaws, etc."
        className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary resize-none"
        rows={3}
        maxLength={2000}
      />

      {/* Price + currency */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="number"
            value={price}
            onChange={e => setPrice(e.target.value)}
            placeholder="Price"
            step="0.01"
            min="0"
            className="w-full bg-background rounded-lg pl-9 pr-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="flex bg-muted rounded-lg p-0.5">
          <button
            onClick={() => setCurrency('USD')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${currency === 'USD' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
          >
            USD
          </button>
          <button
            onClick={() => setCurrency('BCH')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${currency === 'BCH' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
          >
            BCH
          </button>
        </div>
      </div>
      {priceConversion && (
        <p className="text-[11px] text-muted-foreground -mt-1.5">≈ {priceConversion}</p>
      )}

      {/* Condition + Category row */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-muted-foreground font-medium mb-0.5 block">Condition</label>
          <select
            value={condition}
            onChange={e => setCondition(e.target.value)}
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none"
          >
            {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground font-medium mb-0.5 block">Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none"
          >
            {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Location + Shipping */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-muted-foreground font-medium mb-0.5 block">Location</label>
          <input
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder="City, Country"
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground font-medium mb-0.5 block">Shipping</label>
          <input
            value={shipping}
            onChange={e => setShipping(e.target.value)}
            placeholder="e.g. Free, $5 flat, Local only"
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none"
          />
        </div>
      </div>

      {/* Images */}
      <div>
        <label className="text-[10px] text-muted-foreground font-medium mb-1 block">Photos (up to 5)</label>
        <div className="flex gap-2 flex-wrap">
          {images.map((url, i) => (
            <div key={i} className="relative group">
              <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-border" />
              <button
                onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {images.length < 5 && (
            <>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-16 h-16 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex items-center justify-center transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                ) : (
                  <Camera className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
            </>
          )}
        </div>
        {uploading && (
          <div className="h-1 bg-muted rounded-full overflow-hidden mt-1.5">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {error}
        </div>
      )}

      {/* Disclaimer */}
      <div className="rounded-lg bg-amber-500/5 border border-amber-500/15 px-3 py-2">
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          By listing an item, you agree that BCHNostr is not responsible for any transactions, disputes,
          lost, damaged, or stolen goods. All sales are final with no refunds from BCHNostr. You are solely
          responsible for the accuracy of your listing and fulfilling orders.
        </p>
      </div>

      {/* Submit */}
      <button
        onClick={handlePublish}
        disabled={saving || !title.trim() || !price.trim()}
        className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
        {saving ? 'Publishing...' : 'List for Sale'}
      </button>
    </div>
  );
}

// ── Featured Listing Modal ───────────────────────────────────────────
function FeaturedListingModal({ listing, onClose, onSuccess }: {
  listing: any; onClose: () => void; onSuccess: () => void;
}) {
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { publish } = useNostrPublish();
  const [selectedTier, setSelectedTier] = useState(FEATURED_LISTING_TIERS[0].id);
  const [boosting, setBoosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inited, setInited] = useState(false);

  const tier = FEATURED_LISTING_TIERS.find(t => t.id === selectedTier)!;
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = walletBalance >= tier.priceSats;

  const usdStr = (sats: number) => {
    if (!bchPrice) return `${sats.toLocaleString()} sats`;
    const usd = (sats / 1e8) * bchPrice;
    return usd < 0.01 ? `${sats.toLocaleString()} sats` : `$${usd.toFixed(2)}`;
  };

  const handleInit = async () => {
    setInited(true);
    await tipWallet.init().catch(() => {});
  };

  const handleBoost = async () => {
    setBoosting(true);
    setError(null);
    try {
      const txId = await tipWallet.sendBCH(BOOST_WALLET, BigInt(tier.priceSats));
      const now = Math.floor(Date.now() / 1000);
      await publish({
        kind: BOOST_KIND,
        content: JSON.stringify({
          listingDTag: listing.dTag,
          eventId: listing.event.id,
          tier: tier.id,
          priceSats: tier.priceSats,
          expiresAt: now + tier.duration,
          boostedBy: listing.pubkey,
        }),
        tags: [
          ['d', `marketplace-feature-${listing.dTag}`],
          ['t', FEATURED_LISTING_TAG],
          ['t', 'bch'],
          ['e', listing.event.id],
          ['expires', String(now + tier.duration)],
          ['amount', String(tier.priceSats)],
          ['txid', txId || ''],
        ],
        created_at: now,
      });
      tipWallet.refreshBalance();
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Feature failed');
    } finally {
      setBoosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <Rocket className="w-5 h-5 text-amber-500" /> Feature Listing
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <p className="text-xs text-muted-foreground">
          Pay BCH to pin <span className="font-semibold text-foreground">"{listing.title}"</span> to the top of the marketplace with a ✨ Featured badge.
        </p>

        {/* Tier selection */}
        <div className="space-y-2">
          {FEATURED_LISTING_TIERS.map(t => (
            <button
              key={t.id}
              onClick={() => setSelectedTier(t.id)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                selectedTier === t.id ? 'border-amber-400 bg-amber-400/5 ring-1 ring-amber-400/30' : 'border-border hover:border-amber-400/40'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span className="text-xl">{t.emoji}</span>
                <div className="text-left">
                  <p className="text-sm font-semibold">{t.label}</p>
                  <p className="text-[10px] text-muted-foreground">Pinned at top with Featured badge</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold font-mono text-amber-600 dark:text-amber-400">{usdStr(t.priceSats)}</p>
                <p className="text-[10px] text-muted-foreground font-mono">{t.priceSats.toLocaleString()} sats</p>
              </div>
            </button>
          ))}
        </div>

        {/* Wallet */}
        {!tipWallet.ready ? (
          <div className="rounded-lg bg-muted/50 border border-border p-3 text-center">
            <p className="text-xs text-muted-foreground mb-2">Load your wallet to pay</p>
            <button
              onClick={handleInit}
              disabled={inited}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5 mx-auto"
            >
              {inited ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />}
              {inited ? 'Loading...' : 'Load Wallet'}
            </button>
          </div>
        ) : (
          <div className="rounded-lg bg-muted/50 border border-border p-3 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Wallet Balance</span>
            <span className={`text-xs font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
              {usdStr(walletBalance)}
            </span>
          </div>
        )}

        {error && <p className="text-xs text-red-500 text-center">{error}</p>}

        <button
          onClick={handleBoost}
          disabled={!tipWallet.ready || !canAfford || boosting}
          className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {boosting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
          {boosting ? 'Featuring...' : `Feature for ${usdStr(tier.priceSats)}`}
        </button>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────
function formatPrice(price: number, currency: string, bchPrice: number | null): string {
  if (currency === 'BCH') {
    const usd = bchPrice ? price * bchPrice : null;
    return usd ? `${price} BCH ($${usd.toFixed(2)})` : `${price} BCH`;
  }
  return `$${price.toFixed(2)}`;
}

function getTimeAgo(timestamp: number): string {
  const secs = Math.floor(Date.now() / 1000 - timestamp);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return `${Math.floor(secs / 604800)}w ago`;
}

